/**
 * The **Changeset** — a public contract, not an internal helper.
 *
 * This exact shape is what the AI assistant's `propose_changes` tool emits
 * (PLAN §7.1) and what the client renders as diff cards with a live vitals
 * preview (PLAN §4.4). Treat it as versioned API surface: adding an optional
 * field is fine, changing the meaning of one is not.
 *
 * ```jsonc
 * {
 *   "changesetId": "cs-01",
 *   "summary": "Bring IBU into style for an American Pale Ale",
 *   "rationale": "60 min Citra is carrying 42 IBU; the style tops out at 40.",
 *   "changes": [
 *     {
 *       "summary": "Change Citra from 20 g to 17 g",
 *       "command": {
 *         "commandId": "cs-01-0",
 *         "type": "recipe.updateHop",
 *         "timestamp": 1755264000000,
 *         "actor": "assistant",
 *         "payload": { "recipeId": "r1", "itemId": "h1", "changes": { "amountKg": 0.017 } }
 *       }
 *     }
 *   ]
 * }
 * ```
 *
 * ## Rules
 *
 * 1. **Ordered.** The commands apply in array order, each against the state the
 *    previous one produced. A changeset is a little log, not a set.
 * 2. **Atomic.** {@link applyChangeset} applies all of them or none. A
 *    half-accepted proposal is a recipe nobody designed.
 * 3. **Staged, never applied by the proposer.** `propose_changes` returns one
 *    of these; the user accepts; the client then dispatches the commands
 *    through the same {@link applyCommand} the UI uses, so undo, sync and
 *    version history work with no special cases (PLAN §7.1).
 * 4. **Summaries are for humans.** Each entry carries one line of English. The
 *    proposer may write its own; {@link buildChangeset} fills in the reducer's
 *    generated summary when it does not.
 */

import { z } from 'zod';
import type { WorkspaceState } from '../document/workspace';
import { applyCommand, describeCommand } from './apply';
import type { ApplyResult, CommandError } from './envelope';
import { commandError } from './envelope';
import { commandSchema, parseCommand } from './parse';
import type { Command } from './types';

/** One staged change: the command, plus the line a diff card shows. */
export const changesetEntrySchema = z.strictObject({
  /** One line of English describing this change. */
  summary: z.string().min(1).max(300),
  /** The command to apply. */
  command: commandSchema,
});

/** @see changesetEntrySchema */
export type ChangesetEntry = z.infer<typeof changesetEntrySchema>;

/** An ordered batch of commands with human-readable summaries. */
export const changesetSchema = z.strictObject({
  /** Stable id for the proposal, so accept/reject can be addressed. */
  changesetId: z.string().min(1).max(64),
  /** One line describing the batch as a whole, e.g. `Bring IBU into style`. */
  summary: z.string().min(1).max(300),
  /** Why the change is being proposed. Shown under the diff cards; may be empty. */
  rationale: z.string().max(2000),
  /** The changes, in application order. */
  changes: z.array(changesetEntrySchema).min(1).max(200),
});

/** @see changesetSchema */
export type Changeset = z.infer<typeof changesetSchema>;

/**
 * Build a changeset from an ordered command list, generating any summary the
 * caller did not supply.
 *
 * Summaries are generated against the state each command *would* see, so a
 * multi-step proposal reads as a story ("Add 30 g Citra at whirlpool", then
 * "Change Citra from 30 g to 25 g") rather than against a stale snapshot.
 * Commands that fail to apply still get an entry — the changeset is a
 * proposal, and {@link applyChangeset} is where failure is reported.
 *
 * @param state - The workspace the changeset will be applied to.
 * @param changes - Commands in order, optionally with an author-written summary.
 */
export function buildChangeset(
  state: WorkspaceState,
  args: {
    readonly changesetId: string;
    readonly summary: string;
    readonly rationale?: string;
    readonly changes: readonly { readonly command: Command; readonly summary?: string }[];
  },
): Changeset {
  let projected = state;
  const changes: ChangesetEntry[] = [];
  for (const change of args.changes) {
    changes.push({
      summary: change.summary ?? describeCommand(projected, change.command),
      command: change.command,
    });
    const result = applyCommand(projected, change.command);
    if (result.ok) projected = result.state;
  }
  return {
    changesetId: args.changesetId,
    summary: args.summary,
    rationale: args.rationale ?? '',
    changes,
  };
}

/**
 * Apply a changeset atomically.
 *
 * @returns The workspace with every command applied, or the first error with
 *   `error.index` naming the offending entry. The input state is untouched on
 *   failure.
 */
export function applyChangeset(state: WorkspaceState, changeset: Changeset): ApplyResult {
  let current = state;
  for (const [index, entry] of changeset.changes.entries()) {
    const result = applyCommand(current, entry.command);
    if (!result.ok) return { ok: false, error: { ...result.error, index } };
    current = result.state;
  }
  return { ok: true, state: current };
}

/** The result of {@link parseChangeset}. */
export type ChangesetParseResult =
  | { readonly ok: true; readonly changeset: Changeset }
  | { readonly ok: false; readonly error: CommandError };

/**
 * Validate an unknown value as a changeset, reporting per-entry command errors.
 *
 * This is the function the Worker calls on the assistant's `propose_changes`
 * arguments. A model that invents a command name or misspells a field gets a
 * tool error naming the entry and the field, which is exactly the feedback that
 * lets it retry successfully.
 */
export function parseChangeset(value: unknown): ChangesetParseResult {
  const shape = changesetSchema.safeParse(value);
  if (shape.success) return { ok: true, changeset: shape.data };

  // The generic failure is unhelpful when the problem is one bad command, so
  // re-check the entries individually and surface the first real reason.
  const outline = z
    .object({ changes: z.array(z.object({ command: z.unknown() })) })
    .safeParse(value);
  if (outline.success) {
    for (const [index, entry] of outline.data.changes.entries()) {
      const parsed = parseCommand(entry.command);
      if (!parsed.ok) return { ok: false, error: { ...parsed.error, index } };
    }
  }
  return {
    ok: false,
    error: commandError('invalid-payload', 'Not a valid changeset.', {
      issues: shape.error.issues.map((issue) => ({
        path: issue.path.join('.'),
        message: issue.message,
      })),
    }),
  };
}
